import { useEffect, useRef, useState } from 'react';
import getColour from '@lendi-ui/commons/colours';
import Clear from '@lendi-ui/icon/Clear';
import Search from '@lendi-ui/icon/Search';
import Modal from '@lendi-ui/modal';
import Spinner from '@lendi-ui/spinner';
import { Input } from '@lendi-ui/text-input';

import { searchABN, searchABNByName } from '@funnels/api';
import { ABN_CAPTURE_REGEX, ABN_REGEX } from '@funnels/constants';
import { ABNDetail, ABNDetailsSimplified } from '@funnels/types';

import TextInputField from '../../Fields/_uncontrolled/TextInputField';
import HiddenField from '../../Fields/HiddenField';
import { ABNSearchList } from './ABNSearchList';
import { ABNAlert, ABNBtn, SearchIcon } from './style';

interface ABNSearchProps {
  abn: string;
  businessName: string;
  onABNSelect: (businessDetails: ABNDetail) => void;
  onABNUnset: () => void;
  fieldPath?: string; // is used to hook a hidden field so you can check form dirty status
  isDisabled?: boolean;
  value?: string;
  label?: string;
  isOptional?: boolean;
  placeholder?: string;
}

interface SearchResult {
  keywords: string;
  ABNList: ABNDetailsSimplified[];
}

const formatABN = (abn: string | number) =>
  `${abn}`.match(ABN_REGEX) ? `${abn}`.replace(ABN_CAPTURE_REGEX, '$1 $2 $3 $4') : '';

export const ABNSearch = (props: ABNSearchProps) => {
  const {
    abn,
    businessName,
    onABNSelect,
    onABNUnset,
    fieldPath,
    isDisabled,
    value,
    label = 'ABN',
    isOptional = false,
    placeholder = 'Enter business name or search ABN',
  } = props;
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const isSearchAble = searchResult?.keywords !== searchText;

  const search = async () => {
    setErrorMessage(null);
    setSearchResult(null);
    if (!searchText.trim()) return;
    if (searchText.replace(/\s/g, '').match(ABN_REGEX)) {
      await selectABN(searchText.replace(/\s/g, ''));
      return;
    }
    setIsSearching(true);
    const ABNList = await searchABNByName(searchText);
    setSearchResult({
      keywords: searchText,
      ABNList,
    });
    setIsSearching(false);
  };

  const clearSearch = () => {
    setSearchResult(null);
    setSearchText('');
  };

  const selectABN = async (abn: string) => {
    try {
      setIsSearching(true);
      const businessDetails = await searchABN(abn);
      setSearchResult(null);
      setIsModalVisible(false);
      onABNSelect(businessDetails);
    } catch (e: any) {
      setErrorMessage(e.response?.data || e.message);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (searchRef.current && isModalVisible) {
      searchRef.current.focus();
    } else {
      setSearchText('');
      setErrorMessage(null);
    }
  }, [isModalVisible]);

  const getInputButton = () => {
    if (!!abn && !!businessName) {
      return (
        <ABNBtn variant="secondary" size="md" onClick={onABNUnset} data-testid="clear-button">
          <Clear height="1.5em" width="1.5em" color={getColour('interaction', 'main')} />
        </ABNBtn>
      );
    } else if (isSearchAble && !isSearching) {
      return (
        <ABNBtn variant="primary" size="md" onClick={search} data-testid="search-button">
          <Search height="2em" width="2em" />
        </ABNBtn>
      );
    } else if (isSearching) {
      return (
        <ABNBtn variant="primary" size="md" data-testid="spinner">
          <Spinner variant="light" />
        </ABNBtn>
      );
    } else {
      return (
        <ABNBtn variant="secondary" size="md" onClick={clearSearch} data-testid="clear-search">
          <Clear height="1.5em" width="1.5em" color={getColour('interaction', 'main')} />
        </ABNBtn>
      );
    }
  };

  return (
    <div>
      <TextInputField
        placeholder={placeholder}
        label={label}
        isReadOnly={true}
        isDisabled={isDisabled}
        isOptional={isOptional}
        onClick={() => setIsModalVisible(true)}
        before={<SearchIcon height="1.5em" width="1.5em" color={getColour('text', 'subtle')} />}
        value={value ?? (!!abn && !!businessName && !isSearching ? `${businessName} - ${formatABN(abn)}` : '')}
      />
      {!!fieldPath && <HiddenField name={fieldPath} validate={() => null} isOptional={isOptional} />}
      <Modal
        role="dialog"
        isVisible={isModalVisible}
        size="lg"
        isHeaderFixed
        onHide={() => {
          setIsModalVisible(!isModalVisible);
        }}
      >
        <Modal.Header title="Search ABN" size="xs" />
        <Modal.Content>
          {errorMessage && <ABNAlert variant="error" heading={errorMessage} />}
          <Input
            ref={searchRef}
            size="md"
            placeholder="Search business name or ABN"
            onChange={(e) => {
              setSearchText(e.target.value);
              setErrorMessage(null);
            }}
            onKeyPress={(e) => {
              if (e.key.toUpperCase() === 'ENTER') {
                e.preventDefault();
                if (isSearchAble) search();
              }
            }}
            isReadOnly={!!abn && !!businessName}
            isFullWidth
            value={!!abn && !!businessName ? `${businessName} - ${formatABN(abn)}` : searchText}
            after={getInputButton()}
          />
          {!isSearching && searchResult && (
            <ABNSearchList ABNList={searchResult.ABNList} handleABNSelect={selectABN} formatABN={formatABN} />
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};
